import { Component, Input, TemplateRef, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { NgbOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { TranslateModule } from "@ngx-translate/core";
import { Helper } from "src/app/core/helpers/helper";
import { EDeliveryService } from "../../services/e-delivery.service";
import { FormsModule } from "@angular/forms";
import { animate, style, transition, trigger } from "@angular/animations";
import { Cart } from "../../../q-commerce/models/cart.model";
import { Child, Variant, ModifierOption, Modifier, GridChildViewConfig, ButtonConfig } from "../../../q-commerce/models/category-group.model";
import { MainCategory } from "../../../q-commerce/models/main-category.model";
import { ServicesService } from "../../../service/services/services.service";

@Component({
  selector: "app-product-customization-modal",
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule],
  templateUrl: "./product-customization-modal.component.html",
  styleUrl: "./product-customization-modal.component.scss",
  animations: [
    trigger("totalPriceChageAnimation", [
      transition(":increment", [style({ opacity: 0, transform: "scale(1.2)" }), animate("0.3s ease-in", style({ opacity: 1, transform: "scale(1)" }))]),
      transition(":decrement", [style({ opacity: 0, transform: "scale(1.2)" }), animate("0.5s ease-out", style({ opacity: 1, transform: "scale(1)" }))])
    ])
  ]
})
export class ProductCustomizationModalComponent {
  @Input() serviceMainCategory!: MainCategory;
  @Input() gridChildViewConfig!: GridChildViewConfig | null;
  productDetails!: Child;
  selectedVariantId: string = "";
  selectedVariantMaxQtyAddInCart: number = 0;
  selectedVariantModifiers: Modifier[] = [];
  totalProductQuantity: number = 0;
  merchantId: string = "";
  isVariantOutOfStock: boolean = false;
  @ViewChild("customizationModal", { static: true }) customizationModal!: TemplateRef<any>;
  validationErrors: { [key: string]: boolean } = {};

  constructor(private offcanvasService: NgbOffcanvas, public helper: Helper, private edeliveryService: EDeliveryService, private servicesService: ServicesService) {}

  /**
   * Opens the product customization modal with the given product selected.
   * @param product The product to select in the modal.
   */
  openCustomizationModal(product: Child, merchantId: string, selectedVariant?: Variant): void {
    this.offcanvasService.open(this.customizationModal, { position: "end", panelClass: "modal-right-lg", backdrop: "static" });
    this.productDetails = product;
    this.merchantId = merchantId;
    this.productDetails.modifiers.forEach((mod) => {
      mod.options.forEach((option) => {
        option.quantity = 1;
        option.isSelected = option.isDefaultSelected;
      });
    });
    if (this.productDetails.variants && this.productDetails.variants.length > 0) {
      this.productDetails.variants.forEach((variant) => {
        variant.modifiers.forEach((mod) => {
          mod.options.forEach((option) => {
            option.quantity = 1;
            option.isSelected = option.isDefaultSelected;
          });
        });
      });
    }
    this.selectedVariantId = selectedVariant ? selectedVariant._id : this.productDetails.variants[0]._id;
    this.selectedVariantMaxQtyAddInCart = this.productDetails.variants[0].maxQtyAddInCart;
    this.totalProductQuantity = 1;
    this.getVariantMaxQtyAddInCart();
    if (selectedVariant) {
      selectedVariant.modifiers.forEach((mod) => {
        mod.options.forEach((option) => {
          option.quantity = 1;
          option.isSelected = option.isDefaultSelected;
        });
      });
      this.selectVariant(selectedVariant);
    } else {
      this.selectVariant(this.productDetails.variants[0]);
    }
    this.getTotalPrice();
  }

  /**
   * Selects a variant in the product customization modal.
   * Resets all modifier option selections, applies default selections, and ensures all main modifiers are included.
   * @param variant The variant to select.
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  selectVariant(variant: Variant) {
    this.selectedVariantId = variant._id;
    this.selectedVariantMaxQtyAddInCart = variant.maxQtyAddInCart;
    if (variant.status === "OUT_OF_STOCK") {
      this.isVariantOutOfStock = true;
    } else {
      this.isVariantOutOfStock = false;
    }
    this.getVariantMaxQtyAddInCart();
    if (this.totalProductQuantity > this.selectedVariantMaxQtyAddInCart) {
      this.totalProductQuantity = this.selectedVariantMaxQtyAddInCart;
    }
    if (!variant) return;

    // Reset all option selections
    this.productDetails.modifiers.forEach((modifier) => {
      modifier.options.forEach((option) => {
        option.isSelected = false; // Reset selection
        option.quantity = 1; // Reset quantity
      });
    });

    // Clone the original modifiers array to avoid modifying it directly
    this.selectedVariantModifiers = variant.modifiers && variant.modifiers.length > 0 ? [...variant.modifiers] : [];

    // Ensure main modifiers are included
    this.productDetails.modifiers.forEach((mainModifier) => {
      const existingModifier = this.selectedVariantModifiers.find((vMod) => vMod.id === mainModifier.id);
      if (!existingModifier) {
        this.selectedVariantModifiers.push(mainModifier);
      }
    });

    // Apply isDefaultSelected values to isSelected
    this.selectedVariantModifiers.forEach((modifier) => {
      modifier.options.forEach((option) => {
        option.isSelected = option.isDefaultSelected;
      });
    });
    this.getTotalPrice();
  }

  /**
   * Toggles the selection state of a modifier option.
   * If the modifier is a radio button (maxRange === 1 and minRange === 1), it
   * will deselect all other options and select the given option.
   * If the modifier is a checkbox (maxRange > 1 or minRange > 1), it will
   * toggle the selection state of the given option.
   * @param option The modifier option to toggle
   * @param modifier The modifier that the option belongs to
   */
  selectOption(option: ModifierOption, modifier: Modifier): void {
    if (modifier.maxRange === 1 && modifier.minRange === 1) {
      // Radio button case: Ensure only one selection
      modifier.options.forEach((opt) => (opt.isSelected = false)); // Deselect all
      option.isSelected = true;
    } else {
      // Checkbox case: Toggle selection
      option.isSelected = !option.isSelected;
    }
    this.validateModifierSelection(modifier);
    this.getTotalPrice();
  }

  /**
   * Increases the quantity of the specified modifier option by 1.
   * Updates the total product price after the change.
   * @param option The modifier option whose quantity is to be increased.
   */
  increaseQuantity(option: ModifierOption): void {
    option.quantity++;
    this.getTotalPrice();
  }

  /**
   * Decreases the quantity of the specified modifier option by 1.
   * Updates the total product price after the change.
   * @param option The modifier option whose quantity is to be decreased.
   * If the quantity is 1, it does not do anything.
   */
  decreaseQuantity(option: ModifierOption): void {
    if (option.quantity > 1) {
      option.quantity--;
      this.getTotalPrice();
    }
  }

  /**
   * Calculates the total product price based on selected variant and modifiers
   */
  getTotalPrice(): number {
    let basePrice = 0;

    // Get the selected variant's price
    const selectedVariant = this.productDetails.variants.find((v) => v._id === this.selectedVariantId);
    if (selectedVariant) {
      basePrice = selectedVariant.discountedPrice || selectedVariant.price;
    }

    // Add selected modifier options' price
    let modifiersPrice = 0;
    this.selectedVariantModifiers.forEach((modifier) => {
      modifier.options.forEach((option) => {
        if (option.isSelected) {
          modifiersPrice += (option.price || 0) * option.quantity;
        }
      });
    });

    return (basePrice + modifiersPrice) * this.totalProductQuantity;
  }

  /**
   * Increases product quantity and updates total price
   */
  increaseProductQuantity(): void {
    this.totalProductQuantity++;
    this.getTotalPrice();
  }

  /**
   * Decreases product quantity and updates total price
   */
  decreaseProductQuantity(): void {
    if (this.totalProductQuantity > 1) {
      this.totalProductQuantity--;
      this.getTotalPrice();
    }
  }

  /**
   * Adds the selected product variant to the cart after validating the selected modifiers.
   * If validation fails due to incorrect modifier selections, it stops execution.
   * Otherwise, it prepares the cart item with the selected variant details, quantity,
   * and modifiers, and adds it to the cart using the eDeliveryService.
   * Finally, it closes the customization modal.
   */
  addProductToCart(): void {
    this.validationErrors = {}; // Reset errors before validation

    let isValid = true;

    this.selectedVariantModifiers.forEach((modifier) => {
      const selectedOptions = modifier.options.filter((option) => option.isSelected);
      const selectedCount = selectedOptions.length;

      if (selectedCount < modifier.minRange || selectedCount > modifier.maxRange) {
        this.validationErrors[modifier.id] = true; // Mark error for this modifier
        isValid = false;
      }
    });

    if (!isValid) {
      return; // Stop execution if validation fails
    }

    // Continue with adding to cart if validation passes
    const modifiers = this.selectedVariantModifiers
      .filter((modifier) => modifier.options.some((option) => option.isSelected))
      .map((modifier) => ({
        id: modifier.id,
        options: modifier.options
          .filter((option) => option.isSelected)
          .map((option) => ({
            id: option.id,
            price: option.price,
            qty: option.quantity,
            name: option.name
          }))
      }));

    const selectedVariant = this.productDetails.variants.find((v) => v._id === this.selectedVariantId)!;
    if (selectedVariant) {
      const cartItem: Cart = {
        productId: this.productDetails._id,
        variantId: this.selectedVariantId,
        qty: this.totalProductQuantity,
        modifiers: modifiers
      };
      if (this.helper.route.url.includes("/users/create-booking/e-delivery")) {
        cartItem.merchantId = this.merchantId;
        cartItem.price = selectedVariant.discountedPrice || selectedVariant.price;
        this.edeliveryService.addToCart(cartItem, selectedVariant);
      } else if (this.helper.route.url.includes("/users/create-booking/services")) {
        cartItem.price = selectedVariant.discountedPrice || selectedVariant.price;
        if (this.merchantId) {
          cartItem.serviceMerchantId = this.merchantId;
          cartItem.isMerchantService = true;
        }
        this.servicesService.addToCart(cartItem, selectedVariant, this.serviceMainCategory);
      }
      this.closeModal();
    }
  }

  /**
   * Validates the modifier selection.
   * If the number of selected options does not satisfy the modifier's min and max range,
   * it marks the modifier as having a validation error.
   * Otherwise, it removes the error if it was previously marked as invalid.
   * @param modifier The modifier to validate its selection.
   */
  validateModifierSelection(modifier: Modifier): void {
    const selectedCount = modifier.options.filter((option) => option.isSelected).length;
    if (selectedCount < modifier.minRange || selectedCount > modifier.maxRange) {
      this.validationErrors[modifier.id] = true; // Mark error
    } else {
      delete this.validationErrors[modifier.id]; // Remove error if valid
    }
  }

  /**
   * Checks if there are any validation errors for the modifier selection.
   * If there are any errors, it also scrolls to the first error.
   * @returns A boolean indicating whether there are any validation errors.
   */
  hasValidationErrors(): boolean {
    const hasErrors = Object.keys(this.validationErrors).length > 0;
    if (hasErrors) {
      this.scrollToFirstError(); // Scroll to the first error
    }
    return hasErrors;
  }

  /**
   * Scrolls to the first modifier with a validation error, if any.
   * If there are no errors, it does nothing.
   */
  scrollToFirstError(): void {
    const firstErrorId = Object.keys(this.validationErrors)[0]; // Get first modifier with an error
    if (firstErrorId) {
      const element = document.getElementById(`modifier-${firstErrorId}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }

  /**
   * Gets the quantity of the product variant in the cart.
   * If the product variant is not in the cart, it sets the quantity to 1.
   */
  getVariantMaxQtyAddInCart(): void {
    const cartItems = this.getCartItems();
    const cartItem = cartItems.find((item: Cart) => item.productId === this.productDetails._id && item.variantId === this.selectedVariantId);
    const cartItemQuantity = cartItems
      .filter((item: Cart) => item.productId === this.productDetails._id && item.variantId === this.selectedVariantId)
      .reduce((total, item) => total + item.qty, 0);
    this.selectedVariantMaxQtyAddInCart = cartItem && cartItemQuantity ? this.selectedVariantMaxQtyAddInCart - cartItemQuantity : this.selectedVariantMaxQtyAddInCart;
  }

  private getCartItems(): Cart[] {
    if (this.helper.route.url.includes("/users/create-booking/quick-commerce")) {
      return JSON.parse(localStorage.getItem("cartItems") || "[]");
    } else if (this.helper.route.url.includes("/users/create-booking/services")) {
      return JSON.parse(localStorage.getItem("serviceCartItems") || "[]");
    }
    return JSON.parse(localStorage.getItem("deliveryCartItems") || "[]");
  }

  closeModal(): void {
    this.selectedVariantId = "";
    this.offcanvasService.dismiss(this.customizationModal);
  }

  /**
   * Returns the CSS style object for a button based on its configuration.
   * @param buttonConfig The button configuration object.
   * @returns An object with CSS styles for the button, including background color and color.
   */
  getButtonStyle(buttonConfig: ButtonConfig): { [key: string]: string } {
    return {
      backgroundColor: buttonConfig?.bgColor,
      color: buttonConfig?.color,
      border: "0px"
    };
  }
}
